import React, { useMemo } from 'react';
import { Form, Tooltip } from 'antd';
import { usePropagationDimensions } from '@src/emp-3.0/api/queryHooks';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import CustomMultiSelect from './CustomMultiSelect';
import { InfoCircleOutlined } from '@ant-design/icons';
import DimensionItem from './DimensionItem';

interface PropagativeDimension {
  dimension_id: number;
  mapped_event_names: string[];
  propagation_method: 'first' | 'last';
}

interface PropogateDimensionProps {
  disabled?: boolean;
  form: any; // Required form instance
  isView?: boolean;
  eventIds: number[];
  type?: 'Flow' | 'Metric';
}

interface PropagationDimension {
  id: number;
  display_name: string;
  attribute_name: string;
  attribute_type: number;
  category: string;
  dimension_identifier: string;
  dimension_def_identifier: string;
  mapped_event_names: string[];
  dimension_name: string;
  is_beta: boolean;
  would_be_associated: number;
}

const PropogateDimension: React.FC<PropogateDimensionProps> = ({
  isView,
  form,
  eventIds,
  type = 'Flow',
}) => {
  const formValue = Form.useWatch(['definition', 'propagative_dimensions'], form);
  const value = useMemo(() => formValue || [], [formValue]);
  const { data: dimensions = [], isFetching } = usePropagationDimensions(eventIds);

  // Create options for the dimension selector
  const dimensionOptions = useMemo(() => {
    return dimensions.map((dimension: PropagationDimension) => ({
      label: dimension.display_name,
      value: dimension.id,
    }));
  }, [dimensions]);

  // Handle dimension selection - simplified to match WithoutEvent pattern
  const handleDimensionSelection = (selectedIds: number[]) => {
    // Get existing dimensions
    const existingDimensions = value || [];
    const existingDimensionIds = existingDimensions.map(
      (dim: PropagativeDimension) => dim.dimension_id,
    );

    // Find newly added dimensions
    const newDimensionIds = selectedIds.filter((id) => !existingDimensionIds.includes(id));

    // Find removed dimensions
    const removedDimensionIds = existingDimensionIds.filter((id) => !selectedIds.includes(id));

    // Create new dimensions for newly selected ones
    const newDimensions: PropagativeDimension[] = newDimensionIds.map((dimensionId) => ({
      dimension_id: dimensionId,
      mapped_event_names: [],
      propagation_method: 'first' as const,
    }));

    // Keep existing dimensions that weren't removed, and add new ones
    const updatedDimensions = [
      ...existingDimensions.filter(
        (dim: PropagativeDimension) => !removedDimensionIds.includes(dim.dimension_id),
      ),
      ...newDimensions,
    ];

    form.setFieldsValue({
      definition: {
        propagative_dimensions: updatedDimensions,
      },
    });
  };
  const handleDeleteDimension = (dimensionId: number) => {
    const updatedDimensions = value.filter(
      (dimension: PropagativeDimension) => dimension.dimension_id !== dimensionId,
    );
    form.setFieldsValue({
      definition: {
        propagative_dimensions: updatedDimensions,
      },
    });

    // Trigger validation for the propagative_dimensions field
  };

  const handleEventSelection = (dimensionId: number, selectedEvents: string[]) => {
    const updatedDimensions = value.map((dimension: PropagativeDimension) => {
      if (dimension.dimension_id === dimensionId) {
        return {
          ...dimension,
          mapped_event_names: selectedEvents,
        };
      }
      return dimension;
    });
    form.setFieldsValue({
      definition: {
        propagative_dimensions: updatedDimensions,
      },
    });

    // Trigger validation for the propagative_dimensions field
  };

  const handlePropagationMethodChange = (dimensionId: number, method: 'first' | 'last') => {
    const updatedDimensions = value.map((dimension: PropagativeDimension) => {
      if (dimension.dimension_id === dimensionId) {
        return {
          ...dimension,
          propagation_method: method,
        };
      }
      return dimension;
    });
    form.setFieldsValue({
      definition: {
        propagative_dimensions: updatedDimensions,
      },
    });
  };
  // Extract dimension IDs from form field value (PropagativeDimension objects)
  const selectedDimensions = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return [];

    return value
      .filter((item) => item && typeof item === 'object' && item.dimension_id)
      .map((item) => item.dimension_id);
  }, [value]);

  return (
    <div style={{ marginBottom: '24px' }}>
      <div id="propagate_dimension" style={{ display: value.length || !isView ? 'block' : 'none' }}>
        <div style={{ display: 'flex' }}>
          <div
            style={{
              width: '286px',
              display: 'flex',
              alignItems: 'center',
              marginBottom: isView ? 12 : 0,
            }}
          >
            <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
              Additional {type} Dimensions{' '}
              <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>(Optional)</span>
            </span>
            <Tooltip
              title={`Add dimensions that are not automatically derived from the ${type}'s start or end events to enrich the ${type}-level analysis.`}
              placement="rightBottom"
            >
              <InfoCircleOutlined style={{ opacity: '0.4', fontSize: '18px', marginLeft: '8px' }} />
            </Tooltip>
          </div>

          <CustomFormItem
            name={['definition', 'propagative_dimensions']}
            label=""
            rules={[
              {
                validator: (_, value) => {
                  if (!value || !Array.isArray(value) || value.length === 0) {
                    return Promise.resolve();
                  }

                  // Check if any dimension has no mapped events
                  const dimensionsWithoutEvents = value.filter(
                    (dimension: PropagativeDimension) =>
                      !dimension.mapped_event_names || dimension.mapped_event_names.length === 0,
                  );

                  if (dimensionsWithoutEvents.length > 0) {
                    return Promise.reject(
                      new Error('Each selected dimension must have at least one mapped event'),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{
              display: isView ? 'none' : 'block',
              marginBottom: '12px',
              flex: 1,
              paddingTop: '12px',
            }}
          >
            <CustomMultiSelect
              onChange={handleDimensionSelection}
              options={dimensionOptions}
              placeholder="Select dimension(s)"
              disabled={eventIds?.length < 2}
              loading={isFetching}
              emptyText="No dimensions available"
              selectedIds={selectedDimensions}
            />
          </CustomFormItem>
        </div>
        <div>
          {value.map((dimension) => {
            const dimensionDetails = dimensions.find((dim) => dim.id === dimension.dimension_id);
            const selectedMappedEvents = dimension.mapped_event_names || [];
            return (
              <div key={dimension.dimension_id}>
                <DimensionItem
                  selectedMappedEvents={selectedMappedEvents}
                  canEdit={!isView}
                  onDelete={handleDeleteDimension}
                  dimensionDetails={dimensionDetails}
                  handleEventSelection={handleEventSelection}
                  propagationMethod={dimension.propagation_method}
                  onPropagationMethodChange={handlePropagationMethodChange}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PropogateDimension;
